import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as api from "../services/projectApi";

const STEPS = [
  "Project details",
  "Objective",
  "Platforms",
  "KPIs",
  "Budget",
  "Timeline",
  "Business context",
  "Review",
];

const OPERATORS = [">", "<", ">=", "<=", "="];
const emptyKpiDraft = { type: "primary", metric: "", operator: ">", target: "", unit: "", priority: "high" };

export default function CreateProject() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();

  const [projectId, setProjectId] = useState(routeId || null);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [ready, setReady] = useState(!routeId);
  const [autosave, setAutosave] = useState("idle"); // idle | saving | saved
  const savingRef = useRef(false);

  const [objectivesList, setObjectivesList] = useState([]);
  const [maxObjectives, setMaxObjectives] = useState(3);
  const [ecommerceObjectives, setEcommerceObjectives] = useState([]);
  const [kpiMetrics, setKpiMetrics] = useState({});
  const [platformsList, setPlatformsList] = useState([]);
  const [goalConfig, setGoalConfig] = useState({}); // { [objective]: {headline, primary, secondary, autoTracked, ...} }
  // Business-goal answers keyed by objective: { target, targetUnknown, secondary, secondaryUnknown }.
  const [goalAnswers, setGoalAnswers] = useState({});
  const [advancedKpi, setAdvancedKpi] = useState(false); // false = simple (business goals), true = manual KPI editor

  const [details, setDetails] = useState({
    projectName: "",
    businessName: "",
    industry: "",
    website: "",
    currency: "USD",
    description: "",
  });
  const [objectives, setObjectives] = useState([]); // array of objective strings (max 3)
  const [platforms, setPlatforms] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [kpiDraft, setKpiDraft] = useState(emptyKpiDraft);
  const [budget, setBudget] = useState({ currency: "USD", totalBudget: "", dailyBudget: "", google: 60, meta: 40 });
  const [timeline, setTimeline] = useState({ startDate: "", endDate: "", sprintLength: 14 });
  const [context, setContext] = useState({
    product: "", usp: "", audience: "", competitors: "", competitorWebsite: "", regions: "",
    landingPage: "", averageOrderValue: "", seasonalOffer: "", constraints: "", notes: "",
  });

  // True when any selected objective unlocks e-commerce-only fields (AOV).
  const isEcommerce = objectives.some((o) => ecommerceObjectives.includes(o));

  // Load reference lists + existing project (edit mode).
  useEffect(() => {
    api.getObjectivesMeta()
      .then((meta) => {
        setObjectivesList(meta.objectives || []);
        setMaxObjectives(meta.max || 3);
        setEcommerceObjectives(meta.ecommerce || []);
      })
      .catch(() => {});
    api.getKpiMetrics().then(setKpiMetrics).catch(() => {});
    api.getPlatformsList().then(setPlatformsList).catch(() => {});
    api.getGoalConfig().then(setGoalConfig).catch(() => {});
  }, []);

  useEffect(() => {
    if (!routeId) return;
    api.getProject(routeId).then((p) => {
      setDetails({
        projectName: p.projectName || "",
        businessName: p.businessName || "",
        industry: p.industry || "",
        website: p.website || "",
        currency: p.currency || "USD",
        description: p.description || "",
      });
      setObjectives((p.objectives || []).map((o) => o.objective));
      setPlatforms((p.platforms || []).map((x) => x.platform));
      setKpis(p.kpis || []);
      // Hydrate business-goal answers; fall back to Advanced mode if the
      // project has manually-added KPIs but no saved business goals.
      const goals = p.goals || [];
      if (goals.length) {
        const answers = {};
        goals.forEach((g) => {
          answers[g.objective] = {
            target: g.targetValue ?? "",
            targetUnknown: g.targetMode === "ai_recommended",
            secondary: g.secondaryValue ?? "",
            secondaryUnknown: g.secondaryMode === "ai_recommended",
          };
        });
        setGoalAnswers(answers);
      } else if ((p.kpis || []).some((k) => k.source === "manual")) {
        setAdvancedKpi(true);
      }
      if (p.budget) setBudget({
        currency: p.budget.currency, totalBudget: p.budget.totalBudget, dailyBudget: p.budget.dailyBudget || "",
        google: p.budget.googlePercentage, meta: p.budget.metaPercentage,
      });
      if (p.timeline) setTimeline({
        startDate: String(p.timeline.startDate).slice(0, 10),
        endDate: String(p.timeline.endDate).slice(0, 10),
        sprintLength: p.timeline.sprintLength || 14,
      });
      if (p.businessContext) setContext((c) => ({
        ...c,
        product: p.businessContext.product || "", usp: p.businessContext.usp || "",
        audience: p.businessContext.idealCustomer || "", competitors: p.businessContext.competitors || "",
        competitorWebsite: p.businessContext.competitorWebsite || "",
        regions: p.businessContext.regions || "", landingPage: p.businessContext.landingPage || "",
        averageOrderValue: p.businessContext.averageOrderValue || "", seasonalOffer: p.businessContext.seasonalOffer || "",
        constraints: p.businessContext.constraints || "", notes: p.businessContext.notes || "",
      }));
      setReady(true);
    }).catch((e) => { setError(e.response?.data?.message || e.message); setReady(true); });
  }, [routeId]);

  const refreshKpis = useCallback(async (pid) => {
    const p = await api.getProject(pid);
    setKpis(p.kpis || []);
  }, []);

  const togglePlatform = (name) =>
    setPlatforms((cur) => (cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name]));

  // Multi-select objectives, capped at maxObjectives.
  const toggleObjective = (name) =>
    setObjectives((cur) => {
      if (cur.includes(name)) return cur.filter((x) => x !== name);
      if (cur.length >= maxObjectives) return cur; // at the cap — ignore
      return [...cur, name];
    });

  // Objectives (in selection order) that have a business-goal question set.
  const goalObjectives = objectives.filter((o) => goalConfig[o]);

  // Update one field of a goal's answer.
  const setAnswer = (objective, patch) =>
    setGoalAnswers((cur) => ({ ...cur, [objective]: { ...(cur[objective] || {}), ...patch } }));

  // Simple mode is ready once every goal has a target (a number, or "not sure").
  const goalsComplete = () =>
    goalObjectives.length > 0 &&
    goalObjectives.every((o) => {
      const a = goalAnswers[o] || {};
      return a.targetUnknown || (a.target !== "" && a.target != null && Number(a.target) >= 0);
    });

  // Suggested KPI metrics for the selected objectives (deduped).
  const suggestedMetrics = () => {
    const seen = new Set();
    const out = [];
    objectives.forEach((o) => {
      (kpiMetrics[o] || []).forEach((m) => {
        if (!seen.has(m.metric)) {
          seen.add(m.metric);
          out.push(m);
        }
      });
    });
    return out;
  };

  // Persist the current step. Returns true on success.
  const saveStep = async ({ silent = false } = {}) => {
    if (!silent) {
      setError(null);
      setFieldErrors({});
    }
    const pid = projectId;
    try {
      if (step === 1) {
        if (!pid) {
          const created = await api.createProject(details);
          setProjectId(created.id);
        } else {
          await api.updateProject(pid, details);
        }
      } else if (step === 2 && objectives.length) {
        await api.setObjectives(pid, objectives);
      } else if (step === 3 && platforms.length) {
        await api.setPlatforms(pid, platforms);
      } else if (step === 5 && budget.totalBudget) {
        await api.saveBudget(pid, {
          currency: budget.currency,
          totalBudget: Number(budget.totalBudget),
          dailyBudget: budget.dailyBudget ? Number(budget.dailyBudget) : undefined,
          google: Number(budget.google),
          meta: Number(budget.meta),
        });
      } else if (step === 6 && timeline.startDate && timeline.endDate) {
        await api.saveTimeline(pid, {
          startDate: timeline.startDate,
          endDate: timeline.endDate,
          sprintLength: timeline.sprintLength ? Number(timeline.sprintLength) : undefined,
        });
      } else if (step === 7) {
        const payload = {};
        for (const [k, v] of Object.entries(context)) {
          if (v === "" || v == null) continue;
          // Average order value only applies to e-commerce objectives.
          if (k === "averageOrderValue" && !isEcommerce) continue;
          payload[k] = k === "averageOrderValue" ? Number(v) : v;
        }
        await api.saveContext(pid, payload);
      } else if (step === 4 && !advancedKpi) {
        // Simple mode: persist business goals; the server maps them to KPIs.
        const answers = {};
        goalObjectives.forEach((o) => {
          const a = goalAnswers[o] || {};
          answers[o] = {
            target: a.targetUnknown ? undefined : a.target === "" ? undefined : Number(a.target),
            targetUnknown: !!a.targetUnknown,
            secondary: a.secondaryUnknown ? undefined : a.secondary === "" ? undefined : Number(a.secondary),
            secondaryUnknown: !!a.secondaryUnknown,
          };
        });
        await api.setGoals(pid, goalObjectives, answers);
        await refreshKpis(pid);
      }
      // step 4 in Advanced mode saves KPIs inline; nothing to persist here.
      return true;
    } catch (err) {
      if (!silent) {
        const res = err.response?.data;
        setError(res?.message || err.message);
        setFieldErrors(res?.errors || {});
      }
      return false;
    }
  };

  // Autosave the current step every 10s (once the project exists and the
  // step's data is valid). Uses a ref so the interval always runs the latest closure.
  const autosaveRef = useRef(() => {});
  autosaveRef.current = async () => {
    // Skip step 4 (KPIs): Advanced mode saves inline, and Simple mode persists
    // via a full goal→KPI regeneration that shouldn't churn on every tick.
    if (!projectId || step === 4 || step >= STEPS.length || savingRef.current || !canAdvance()) return;
    savingRef.current = true;
    setAutosave("saving");
    const ok = await saveStep({ silent: true });
    setAutosave(ok ? "saved" : "idle");
    savingRef.current = false;
  };
  useEffect(() => {
    const t = setInterval(() => autosaveRef.current(), 10000);
    return () => clearInterval(t);
  }, []);

  // Client-side gate before advancing.
  const canAdvance = () => {
    if (step === 1) return details.projectName.trim() && details.businessName.trim();
    if (step === 2) return objectives.length > 0;
    if (step === 3) return platforms.length > 0;
    if (step === 4) return advancedKpi ? kpis.length > 0 : goalsComplete();
    if (step === 5) return Number(budget.totalBudget) > 0 && Number(budget.google) + Number(budget.meta) === 100;
    if (step === 6) return timeline.startDate && timeline.endDate && timeline.endDate > timeline.startDate;
    return true;
  };

  const next = async () => {
    if (!canAdvance()) {
      setError("Please complete this step before continuing.");
      return;
    }
    savingRef.current = true; // block autosave from racing this save
    setBusy(true);
    const ok = await saveStep();
    setBusy(false);
    savingRef.current = false;
    if (ok) setStep((s) => Math.min(s + 1, STEPS.length));
  };

  const back = () => setStep((s) => Math.max(s - 1, 1));

  const addKpi = async () => {
    if (!kpiDraft.metric.trim()) return;
    setBusy(true);
    try {
      await api.addKpi(projectId, {
        ...kpiDraft,
        target: kpiDraft.target ? Number(kpiDraft.target) : undefined,
      });
      setKpiDraft(emptyKpiDraft);
      await refreshKpis(projectId);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const removeKpi = async (kpiId) => {
    await api.deleteKpi(kpiId);
    await refreshKpis(projectId);
  };

  const finish = () => navigate(`/projects/${projectId}`);

  if (!ready) return <div className="proj-center proj-muted">Loading…</div>;

  const budgetTotal = Number(budget.google) + Number(budget.meta);

  return (
    <div className="proj-page">
      <div className="page-header">
        <div>
          <button className="proj-link" onClick={() => navigate("/projects")}>← Projects</button>
          <h1 className="page-title">{routeId ? "Edit project" : "New project"}</h1>
          <p className="proj-muted">
            Step {step} of {STEPS.length} · {STEPS[step - 1]}
            {projectId && autosave !== "idle" && (
              <span className={`wiz-autosave ${autosave}`}>
                {" · "}
                {autosave === "saving" ? "Saving…" : "Draft saved"}
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="proj-body">
        {/* Stepper */}
        <div className="wiz-stepper">
          {STEPS.map((label, i) => (
            <div
              key={i}
              className={`wiz-step ${i + 1 === step ? "active" : ""} ${i + 1 < step ? "done" : ""}`}
              onClick={() => projectId && setStep(i + 1)}
            >
              <span className="wiz-step-num">{i + 1 < step ? "✓" : i + 1}</span>
              <span className="wiz-step-label">{label}</span>
            </div>
          ))}
        </div>

        <div className="proj-panel wiz-panel">
          {error && <div className="proj-alert">{error}</div>}

          {/* Step 1 — details */}
          {step === 1 && (
            <div className="proj-form">
              <Field label="Project name *" err={fieldErrors.projectName}>
                <input value={details.projectName} onChange={(e) => setDetails({ ...details, projectName: e.target.value })} />
              </Field>
              <Field label="Business name *" err={fieldErrors.businessName}>
                <input value={details.businessName} onChange={(e) => setDetails({ ...details, businessName: e.target.value })} />
              </Field>
              <div className="wiz-row">
                <Field label="Industry">
                  <input value={details.industry} onChange={(e) => setDetails({ ...details, industry: e.target.value })} placeholder="e.g. E-commerce" />
                </Field>
                <Field label="Currency">
                  <input value={details.currency} onChange={(e) => setDetails({ ...details, currency: e.target.value.toUpperCase() })} maxLength={3} />
                </Field>
              </div>
              <Field label="Website" err={fieldErrors.website}>
                <input value={details.website} onChange={(e) => setDetails({ ...details, website: e.target.value })} placeholder="https://…" />
              </Field>
              <Field label="Description">
                <textarea rows={3} value={details.description} onChange={(e) => setDetails({ ...details, description: e.target.value })} />
              </Field>
            </div>
          )}

          {/* Step 2 — objectives (multi-select, max 3) */}
          {step === 2 && (
            <div>
              <p className="proj-muted" style={{ marginBottom: 12 }}>
                Select up to {maxObjectives} objectives ({objectives.length}/{maxObjectives} chosen).
              </p>
              <div className="wiz-cards">
                {objectivesList.map((o) => {
                  const sel = objectives.includes(o);
                  const atCap = !sel && objectives.length >= maxObjectives;
                  return (
                    <button
                      key={o}
                      type="button"
                      className={`wiz-card ${sel ? "sel" : ""}`}
                      disabled={atCap}
                      style={atCap ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
                      onClick={() => toggleObjective(o)}
                    >
                      {sel ? "✓ " : ""}
                      {o}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3 — platforms */}
          {step === 3 && (
            <div className="wiz-checks">
              {platformsList.map((p) => (
                <label key={p} className={`wiz-check ${platforms.includes(p) ? "sel" : ""}`}>
                  <input type="checkbox" checked={platforms.includes(p)} onChange={() => togglePlatform(p)} />
                  {p}
                </label>
              ))}
            </div>
          )}

          {/* Step 4 — KPIs (business-goal first) */}
          {step === 4 && (
            <div>
              <div className="wiz-kpi-head">
                <div>
                  <h3 className="wiz-kpi-title">Tell us what success looks like</h3>
                  <p className="proj-muted">
                    Describe your goals in plain language — AdIntel automatically tracks the
                    advertising metrics needed to measure them.
                  </p>
                </div>
                <button
                  type="button"
                  className="proj-link"
                  onClick={() => setAdvancedKpi((v) => !v)}
                >
                  {advancedKpi ? "← Simple setup" : "Advanced setup →"}
                </button>
              </div>

              {objectives.length === 0 && (
                <p className="proj-alert-soft">Select at least one objective in Step 2 first.</p>
              )}

              {/* Simple mode — one card per selected objective */}
              {!advancedKpi && goalObjectives.map((o) => {
                const cfg = goalConfig[o];
                const a = goalAnswers[o] || {};
                return (
                  <div key={o} className="wiz-goal-card">
                    <div className="wiz-goal-head">
                      <strong>{cfg.headline}</strong>
                      <span className="proj-muted"> · {o}</span>
                    </div>
                    <p className="proj-muted wiz-goal-desc">{cfg.description}</p>

                    {/* Primary question */}
                    <label className="wiz-goal-q">
                      <span>{cfg.primary.label}</span>
                      <span className="wiz-goal-input">
                        <input
                          type="number"
                          min="0"
                          placeholder={cfg.primary.placeholder}
                          value={a.targetUnknown ? "" : a.target ?? ""}
                          disabled={a.targetUnknown}
                          onChange={(e) => setAnswer(o, { target: e.target.value })}
                        />
                        <em className="proj-muted">{cfg.primary.unitLabel}</em>
                      </span>
                    </label>

                    {/* Secondary question (supports "I don't know yet") */}
                    {cfg.secondary && (
                      <label className="wiz-goal-q">
                        <span>{cfg.secondary.label}</span>
                        <span className="wiz-goal-input">
                          <input
                            type="number"
                            min="0"
                            placeholder={cfg.secondary.placeholder}
                            value={a.secondaryUnknown ? "" : a.secondary ?? ""}
                            disabled={a.secondaryUnknown}
                            onChange={(e) => setAnswer(o, { secondary: e.target.value })}
                          />
                          {cfg.secondary.hint && <em className="proj-muted">{cfg.secondary.hint}</em>}
                        </span>
                      </label>
                    )}
                    {cfg.secondary?.allowUnknown && (
                      <label className="wiz-goal-unknown">
                        <input
                          type="checkbox"
                          checked={!!a.secondaryUnknown}
                          onChange={(e) => setAnswer(o, { secondaryUnknown: e.target.checked })}
                        />
                        I'm not sure — let AdIntel recommend a starting target
                      </label>
                    )}

                    {/* What AdIntel will track */}
                    <div className="wiz-goal-track">
                      <span className="proj-muted">AdIntel will automatically track:</span>
                      <div className="wiz-goal-chips">
                        {cfg.trackSummary.map((t) => (
                          <span key={t} className="wiz-track-chip">✓ {t}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Advanced mode — manual technical KPI editor */}
              {advancedKpi && (
                <>
                  {suggestedMetrics().length > 0 && (
                    <div className="wiz-kpi-suggest">
                      <span className="proj-muted">Suggested for your objectives:</span>
                      {suggestedMetrics().map((m) => (
                        <button
                          key={m.metric}
                          type="button"
                          className="wiz-suggest-chip"
                          onClick={() =>
                            setKpiDraft((d) => ({ ...d, metric: m.metric, unit: m.unit || "", operator: m.operator || ">" }))
                          }
                        >
                          + {m.metric}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="wiz-kpi-list">
                    {kpis.map((k) => (
                      <div key={k.id} className="wiz-kpi-row">
                        <span className="proj-chip">{k.type}</span>
                        <strong>{k.metric}</strong> {k.operator} {k.target} {k.unit}
                        <span className="proj-muted">({k.priority})</span>
                        {k.source === "business" && <span className="proj-chip">auto</span>}
                        <button className="proj-link proj-link-danger" onClick={() => removeKpi(k.id)}>remove</button>
                      </div>
                    ))}
                    {kpis.length === 0 && <p className="proj-muted">No KPIs yet — add at least one.</p>}
                  </div>
                  <div className="wiz-kpi-form">
                    <input placeholder="Metric (e.g. ROAS)" value={kpiDraft.metric} onChange={(e) => setKpiDraft({ ...kpiDraft, metric: e.target.value })} />
                    <select value={kpiDraft.type} onChange={(e) => setKpiDraft({ ...kpiDraft, type: e.target.value })}>
                      <option value="primary">primary</option>
                      <option value="secondary">secondary</option>
                    </select>
                    <select value={kpiDraft.operator} onChange={(e) => setKpiDraft({ ...kpiDraft, operator: e.target.value })}>
                      {OPERATORS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <input placeholder="Target" value={kpiDraft.target} onChange={(e) => setKpiDraft({ ...kpiDraft, target: e.target.value })} style={{ width: 90 }} />
                    <input placeholder="Unit" value={kpiDraft.unit} onChange={(e) => setKpiDraft({ ...kpiDraft, unit: e.target.value })} style={{ width: 70 }} />
                    <select value={kpiDraft.priority} onChange={(e) => setKpiDraft({ ...kpiDraft, priority: e.target.value })}>
                      <option value="high">high</option>
                      <option value="medium">medium</option>
                      <option value="low">low</option>
                    </select>
                    <button className="proj-btn" onClick={addKpi} disabled={busy || !kpiDraft.metric.trim()}>Add</button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 5 — budget */}
          {step === 5 && (
            <div className="proj-form">
              <div className="wiz-row">
                <Field label="Currency">
                  <input value={budget.currency} onChange={(e) => setBudget({ ...budget, currency: e.target.value.toUpperCase() })} maxLength={3} />
                </Field>
                <Field label="Total budget *" err={fieldErrors.totalBudget}>
                  <input type="number" value={budget.totalBudget} onChange={(e) => setBudget({ ...budget, totalBudget: e.target.value })} />
                </Field>
                <Field label="Daily budget">
                  <input type="number" value={budget.dailyBudget} onChange={(e) => setBudget({ ...budget, dailyBudget: e.target.value })} />
                </Field>
              </div>
              <div className="wiz-row">
                <Field label="Google %">
                  <input type="number" value={budget.google} onChange={(e) => setBudget({ ...budget, google: e.target.value, meta: 100 - Number(e.target.value) })} />
                </Field>
                <Field label="Meta %">
                  <input type="number" value={budget.meta} onChange={(e) => setBudget({ ...budget, meta: e.target.value, google: 100 - Number(e.target.value) })} />
                </Field>
              </div>
              <p className={budgetTotal === 100 ? "proj-muted" : "proj-err"}>
                Allocation total: {budgetTotal}% {budgetTotal === 100 ? "✓" : "(must equal 100%)"}
              </p>
            </div>
          )}

          {/* Step 6 — timeline */}
          {step === 6 && (
            <div className="proj-form">
              <div className="wiz-row">
                <Field label="Start date *">
                  <input type="date" value={timeline.startDate} onChange={(e) => setTimeline({ ...timeline, startDate: e.target.value })} />
                </Field>
                <Field label="End date *">
                  <input type="date" value={timeline.endDate} onChange={(e) => setTimeline({ ...timeline, endDate: e.target.value })} />
                </Field>
                <Field label="Sprint length (days)">
                  <input type="number" value={timeline.sprintLength} onChange={(e) => setTimeline({ ...timeline, sprintLength: e.target.value })} />
                </Field>
              </div>
              {timeline.startDate && timeline.endDate && timeline.endDate <= timeline.startDate && (
                <p className="proj-err">End date must be after start date.</p>
              )}
            </div>
          )}

          {/* Step 7 — business context */}
          {step === 7 && (
            <div className="proj-form">
              <div className="wiz-row">
                <Field label="Product"><input value={context.product} onChange={(e) => setContext({ ...context, product: e.target.value })} /></Field>
                <Field label="USP"><input value={context.usp} onChange={(e) => setContext({ ...context, usp: e.target.value })} /></Field>
              </div>
              <Field label="Target audience"><input value={context.audience} onChange={(e) => setContext({ ...context, audience: e.target.value })} /></Field>
              <div className="wiz-row">
                <Field label="Competitors"><input value={context.competitors} onChange={(e) => setContext({ ...context, competitors: e.target.value })} placeholder="e.g. Acme, BigCo" /></Field>
                <Field label="Competitor website"><input value={context.competitorWebsite} onChange={(e) => setContext({ ...context, competitorWebsite: e.target.value })} placeholder="https://competitor.com" /></Field>
              </div>
              <div className="wiz-row">
                <Field label="Regions"><input value={context.regions} onChange={(e) => setContext({ ...context, regions: e.target.value })} /></Field>
                <Field label="Landing page"><input value={context.landingPage} onChange={(e) => setContext({ ...context, landingPage: e.target.value })} placeholder="https://…" /></Field>
                {isEcommerce && (
                  <Field label="Avg order value (e-commerce)">
                    <input type="number" value={context.averageOrderValue} onChange={(e) => setContext({ ...context, averageOrderValue: e.target.value })} />
                  </Field>
                )}
              </div>
              <Field label="Seasonal offer"><input value={context.seasonalOffer} onChange={(e) => setContext({ ...context, seasonalOffer: e.target.value })} /></Field>
              <Field label="Constraints"><input value={context.constraints} onChange={(e) => setContext({ ...context, constraints: e.target.value })} placeholder="e.g. low budget, no video" /></Field>
              <Field label="Additional notes"><textarea rows={3} value={context.notes} onChange={(e) => setContext({ ...context, notes: e.target.value })} /></Field>
            </div>
          )}

          {/* Step 8 — review */}
          {step === 8 && (
            <div className="wiz-review">
              <Review label="Project" value={`${details.projectName} · ${details.businessName}${details.industry ? " · " + details.industry : ""}`} />
              <Review label="Objectives" value={objectives.join(", ") || "—"} />
              <Review label="Platforms" value={platforms.join(", ") || "—"} />
              {!advancedKpi && goalObjectives.length > 0 ? (
                <Review
                  label="Goals"
                  value={
                    goalObjectives
                      .map((o) => {
                        const cfg = goalConfig[o];
                        const a = goalAnswers[o] || {};
                        const t = a.targetUnknown ? "AdIntel to recommend" : `${a.target || "—"} ${cfg.primary.unitLabel}`;
                        return `${cfg.headline}: ${t}`;
                      })
                      .join(" · ") || "—"
                  }
                />
              ) : (
                <Review
                  label="KPIs"
                  value={kpis.filter((k) => k.target != null).map((k) => `${k.metric} ${k.operator} ${k.target}`).join(", ") || "—"}
                />
              )}
              <Review label="Budget" value={budget.totalBudget ? `${budget.currency} ${budget.totalBudget} · Google ${budget.google}% / Meta ${budget.meta}%` : "—"} />
              <Review label="Timeline" value={timeline.startDate ? `${timeline.startDate} → ${timeline.endDate}` : "—"} />
              <Review label="Product" value={context.product || "—"} />
              <p className="proj-muted" style={{ marginTop: 12 }}>
                Everything's saved. Open the project to generate its AI marketing strategy.
              </p>
            </div>
          )}
        </div>

        {/* Nav */}
        <div className="wiz-nav">
          <button className="proj-btn" onClick={back} disabled={step === 1 || busy}>← Back</button>
          {step < STEPS.length ? (
            <button className="proj-btn proj-btn-primary" onClick={next} disabled={busy}>
              {busy ? "Saving…" : "Save & Next →"}
            </button>
          ) : (
            <button className="proj-btn proj-btn-primary" onClick={finish}>Finish & open project</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, err, children }) {
  return (
    <label className="proj-field">
      <span>{label}</span>
      {children}
      {err && <em className="proj-err">{err[0]}</em>}
    </label>
  );
}

function Review({ label, value }) {
  return (
    <div className="wiz-review-row">
      <span className="proj-muted">{label}</span>
      <span>{value}</span>
    </div>
  );
}
